import {useCallback, useEffect, useMemo, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {MatchFilterOptions} from '../features/company/recruiting/matches/MatchFilters/MatchFilters';
import {mapTalentPoolApiToPaginationResponse} from '../features/company/recruiting/talent-pool/serializers/TalentPoolSerializers';
import {
  TALENT_POOL_STATUS_IDS,
  TalentPoolFilterData,
  TalentPoolListItem,
  TalentPoolPaginationStatusCounts,
} from '../features/company/recruiting/talent-pool/types/TalentPoolTypes';
import {GetRoutes} from '../lib/api/GET/GetRoutes';
import {AppConfig} from '../lib/AppConfig';
import {Option} from '../types/shared-types';

import {PaginationResponse, useQueryPagination} from './useQueryPagination';
import {LocalQueryParams, useQueryParameterState} from './useQueryParameterState';

/** URL query keys scoped to Talent Pool so they do not clash with `/recruiting/matches`. */
interface TalentPoolQueryParams extends LocalQueryParams {
  tp_job?: string;
  tp_status?: string;
  /** 1-based table page; omitted or empty means page 1. */
  tp_page?: string;
}

export const useTalentPoolSorting = (companyId: string | undefined) => {
  const [rows, setRows] = useState<TalentPoolListItem[] | null>(null);
  const [initialJobs, setInitialJobs] = useState<Option[]>([]);
  const [initialStatuses, setInitialStatuses] = useState<Option[]>([]);
  const {t: tStatuses} = useTranslation('translation', {keyPrefix: 'talentPoolPage.statuses'});
  const {updateQueryParameters, params} = useQueryParameterState<TalentPoolQueryParams>();

  const hasListFilters = useMemo(() => Boolean(params.tp_job || params.tp_status), [params.tp_job, params.tp_status]);

  const {
    data,
    pagination,
    hasNext,
    getNext,
    filterData,
    error,
    loadingInitialData,
    refreshData,
    isLoading: isTalentPoolLoading,
  } = useQueryPagination<TalentPoolListItem, TalentPoolPaginationStatusCounts, object, TalentPoolFilterData>({
    url: GetRoutes.companyTalentPool(companyId ?? ''),
    entity: 'talent-pool',
    perPage: AppConfig.PAGINATION_PER_PAGE,
    reverseOrder: true,
    enabled: !!companyId,
    mapResponse: (raw) =>
      mapTalentPoolApiToPaginationResponse(raw) as PaginationResponse<
        TalentPoolListItem,
        TalentPoolPaginationStatusCounts,
        TalentPoolFilterData
      >,
  });

  const tablePage = useMemo(() => {
    const parsed = parseInt(params.tp_page ?? '', 10);
    return Number.isFinite(parsed) && parsed >= 1 ? parsed : 1;
  }, [params.tp_page]);

  const setTablePage = useCallback(
    (nextPage: number) => {
      const safe = Math.max(1, nextPage);
      updateQueryParameters({
        tp_job: params.tp_job ?? '',
        tp_status: params.tp_status ?? '',
        tp_page: safe <= 1 ? '' : String(safe),
      });
    },
    [updateQueryParameters, params.tp_job, params.tp_status]
  );

  useEffect(() => {
    const job = params.tp_job?.split(',').filter(Boolean) || [];
    const status = params.tp_status?.split(',').filter(Boolean) || [];

    setInitialJobs(job.map((id) => ({id, label: id})));
    setInitialStatuses(
      status.map((id) => ({
        id,
        label: String(tStatuses(id as 'pool' | 'waiting_cv' | 'match')),
      }))
    );
  }, [params.tp_job, params.tp_status, tStatuses]);

  useEffect(() => {
    if (!data) return;
    const job = params.tp_job?.split(',').filter(Boolean) || [];
    const status = params.tp_status?.split(',').filter(Boolean) || [];
    let filtered = data;

    if (job.length) {
      filtered = filtered.filter((entry) => job.some((title) => title === entry.campaignName));
    }
    if (status.length) {
      filtered = filtered.filter((entry) => status.some((statusName) => statusName === entry.status));
    }
    setRows([...filtered]);
  }, [data, params.tp_job, params.tp_status]);

  useEffect(() => {
    if (hasListFilters && hasNext && !isTalentPoolLoading) {
      getNext();
    }
  }, [hasListFilters, hasNext, isTalentPoolLoading, getNext]);

  const filterBy = useCallback(
    (filters: MatchFilterOptions) => {
      const {job, status} = filters;

      if (!job.length && !status.length) {
        updateQueryParameters({tp_job: '', tp_status: '', tp_page: ''});
        return;
      }

      updateQueryParameters({
        tp_status: status?.map((option) => option.id).join(','),
        tp_job: job?.map((option) => option.id).join(','),
        tp_page: '',
      });
    },
    [updateQueryParameters]
  );

  const jobOptions: Option[] = useMemo(() => {
    if (filterData?.campaigns?.length) {
      const seen = new Set<string>();
      const opts: Option[] = [];
      for (const c of filterData.campaigns) {
        const title = c.title?.trim();
        if (title && !seen.has(title)) {
          seen.add(title);
          opts.push({id: title, label: title});
        }
      }
      if (opts.length) return opts;
    }
    const seen = new Set<string>();
    const opts: Option[] = [];
    for (const entry of data) {
      const name = entry.campaignName;
      if (name && !seen.has(name)) {
        seen.add(name);
        opts.push({id: name, label: name});
      }
    }
    return opts;
  }, [data, filterData?.campaigns]);

  const statusOptions: Option[] = useMemo(() => {
    if (filterData?.statuses?.length) {
      return filterData.statuses.map((status) => ({
        id: status,
        label: TALENT_POOL_STATUS_IDS.has(status)
          ? String(tStatuses(status as 'pool' | 'waiting_cv' | 'match'))
          : status.replace(/_/g, ' '),
      }));
    }
    return [
      {id: 'pool', label: String(tStatuses('pool'))},
      {id: 'waiting_cv', label: String(tStatuses('waiting_cv'))},
      {id: 'match', label: String(tStatuses('match'))},
    ];
  }, [filterData?.statuses, tStatuses]);

  return {
    talentPool: rows,
    talentPoolCount: data.length,
    pagination,
    hasNext,
    getNext,
    loadingInitialData,
    error,
    refreshData,
    filterBy,
    hasListFilters,
    jobOptions,
    statusOptions,
    initialJobs,
    initialStatuses,
    tablePage,
    setTablePage,
  };
};
